// shared/nav.ts — the built-in back-office navigation.
//
// Lives in shared/ because BOTH sides need it: the app renders from it, and the
// worker merges admin overrides (ui_nav_overrides) on top of it before sending
// the navigation back. Keeping one copy is what makes "reset to default" and
// "a new page appears automatically" both true.
//
// This is the DEFAULT, never the whole truth — see worker/routes/navigation.ts.
export interface NavItem { label: string; to: string; desc: string; icon: string; permission?: string }
export interface NavSection { label: string; icon: string; items: NavItem[] }

export const DEFAULT_NAV: NavSection[] = [
  { label: "Sales", icon: "transactions", items: [
    { label: "Transactions", to: "/transactions", desc: "View, correct & re-link sales", icon: "transactions" },
    { label: "Card payments", to: "/payments", desc: "Terminal & reconciliation", icon: "card" },
    { label: "Web orders", to: "/orders", desc: "Online order inbox", icon: "orders" },
  ] },
  { label: "Workshop", icon: "workshop", items: [
    { label: "Jobs", to: "/workshop", desc: "Job cards & progress", icon: "workshop" },
    { label: "Bay board", to: "/board", desc: "Who is on what, right now", icon: "board" },
    { label: "Diary", to: "/workshop/diary", desc: "Bookings & appointments", icon: "diary" },
    { label: "Reminders", to: "/workshop/reminders", desc: "Service & MOT follow-ups", icon: "reminders" },
    { label: "MOT", to: "/mot", desc: "MOT history & charges", icon: "reports" },
  ] },
  { label: "Stock", icon: "catalogue", items: [
    { label: "Products", to: "/catalogue", desc: "Catalogue & pricing", icon: "products" },
    { label: "Service kits", to: "/kits", desc: "Job bundles", icon: "kits" },
    { label: "QR labels", to: "/qr-labels", desc: "Printable shelf labels & shop code", icon: "products" },
    { label: "Stock counts", to: "/stock-count", desc: "Blind shelf counts & posting", icon: "products" },
    { label: "Lost / found / damaged", to: "/stock-incidents", desc: "Missing, found & damaged stock", icon: "products" },
    { label: "Waste log", to: "/waste", desc: "Duty-of-care disposal", icon: "waste" },
  ] },
  { label: "Purchasing", icon: "purchasing", items: [
    { label: "Purchase orders", to: "/purchasing", desc: "POs & goods-in", icon: "purchasing" },
    { label: "Suppliers", to: "/suppliers", desc: "Supplier directory", icon: "purchasing" },
  ] },
  { label: "Bikes", icon: "bikes", items: [
    { label: "Bikes & rentals", to: "/bikes", desc: "Units, sales, hire", icon: "bikes" },
    { label: "Garage", to: "/garage", desc: "Customer bikes: specs, MOT & history", icon: "bikes" },
  ] },
  { label: "Customers", icon: "customers", items: [
    { label: "Customers", to: "/customers", desc: "Profiles & promotions", icon: "customers" },
    { label: "Accounts", to: "/accounts", desc: "Credit & balances", icon: "accounts" },
  ] },
  { label: "Finance", icon: "finance", items: [
    { label: "Reports", to: "/reports", desc: "Sales & analytics", icon: "reports" },
    { label: "Finances", to: "/finance", desc: "Income, expenses & P&L", icon: "finance" },
    { label: "Expenses", to: "/expenses", desc: "Costs & overheads", icon: "finance" },
    { label: "Close shift", to: "/shifts", desc: "Count the drawer & save the Z-report", icon: "shift" },
    { label: "Bank", to: "/bank", desc: "Statement beside the books — what reached the account", icon: "finance" },
  ] },
  { label: "Admin", icon: "admin", items: [
    { label: "Settings", to: "/settings", desc: "Business, devices & workflow controls", icon: "settings" },
    { label: "Navigation", to: "/navigation", desc: "Rename, reorder & hide pages", icon: "settings", permission: "settings.manage" },
    { label: "Wording", to: "/wording", desc: "Rename anything the app says", icon: "glossary", permission: "settings.manage" },
    { label: "Staff", to: "/staff", desc: "Users & roles", icon: "staff" },
    { label: "Website", to: "/website", desc: "Content & integrations", icon: "website" },
    { label: "WhatsApp", to: "/whatsapp", desc: "Link the shop's WhatsApp, group alerts & messages", icon: "website", permission: "settings.manage" },
    { label: "Records", to: "/records", desc: "All data — view, edit & link", icon: "records" },
    { label: "Activity log", to: "/logs", desc: "Audit trail & stock moves", icon: "logs" },
    { label: "Import", to: "/import", desc: "Bulk CSV in", icon: "importer" },
    { label: "Export", to: "/export", desc: "Backups & CSV", icon: "exporter" },
    { label: "Data & Audit", to: "/audit", desc: "Download, fix & re-upload", icon: "audit" },
    { label: "System audit", to: "/system-audit", desc: "Live integrity checks", icon: "audit" },
    { label: "Glossary", to: "/glossary", desc: "What every term means", icon: "glossary" },
  ] },
];
